import { numberQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Box,
  Button,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SafeDateTimePicker } from "components/SafeDateTimePicker";
import dayjs from "dayjs";
import { sorted } from "helpers";
import { useState } from "react";
import { useTenantTranslation } from "hooks/formatters";
import type {
  SensorTopology,
  SensorTopologyEventKind,
} from "src/store/api/generatedApi";
import {
  formatInstallationTimestamp,
  installationInstant,
} from "./installationTimestamp";

export const EVENT_LABELS: Record<SensorTopologyEventKind, string> = {
  connected: "topologyConnected",
  disconnected: "topologyDisconnected",
  installed: "topologyInstalled",
  removed: "topologyRemoved",
};

interface SensorTopologyTimelineProps {
  topology: SensorTopology;
  sensorId: number | null;
  at: string;
  onChange: (at: string) => void;
}

export const SensorTopologyTimeline = ({
  topology,
  sensorId,
  at,
  onChange,
}: SensorTopologyTimelineProps) => {
  const { t } = useTenantTranslation();
  const [now] = useState(() => Date.now());
  const [windowDays, setWindowDays] = useNavigationQuery(
    "installationWindow",
    0,
    numberQuery([0, 1, 7, 30, 365]),
    "replace"
  );
  const [cursor, setCursor] = useState<number | null>(null);
  const selectedTime = installationInstant(at);
  const events = topology.events.filter(
    (event) => sensorId == null || event.sensor_id === sensorId
  );
  const eventTimes = sorted(
    [...new Set(events.map((event) => event.at))],
    installationInstant
  );
  const times = eventTimes.map(installationInstant);
  const earliest = Math.min(times[0] ?? selectedTime, selectedTime);
  const latest = Math.max(times.at(-1) ?? selectedTime, selectedTime, now);
  const halfWindow = (windowDays * 86400000) / 2;
  const min = windowDays === 0 ? earliest : selectedTime - halfWindow;
  const max =
    windowDays === 0
      ? Math.max(latest, earliest + 1000)
      : selectedTime + halfWindow;
  const previous = eventTimes
    .filter((time) => installationInstant(time) < selectedTime)
    .at(-1);
  const next = eventTimes.find(
    (time) => installationInstant(time) > selectedTime
  );
  const visibleTimes = eventTimes.filter(
    (time) =>
      installationInstant(time) >= min && installationInstant(time) <= max
  );
  const currentEvents = events.filter(
    (event) => installationInstant(event.at) === selectedTime
  );
  const intervals =
    sensorId == null
      ? []
      : [
          ...topology.connections
            .filter((connection) => connection.sensor_id === sensorId)
            .map((connection) => ({
              key: `connection-${connection.id}`,
              label: connection.device_name,
              start: connection.connected_at,
              end: connection.disconnected_at,
              colour: "primary.main",
            })),
          ...topology.installations
            .filter((installation) => installation.sensor_id === sensorId)
            .map((installation) => ({
              key: `installation-${installation.id}`,
              label: `${installation.location_name} / ${installation.recording_position_name}`,
              start: installation.installed_at,
              end: installation.removed_at,
              colour: "success.main",
            })),
        ];
  const percentage = (time: number) =>
    Math.max(0, Math.min(100, (100 * (time - min)) / (max - min)));
  return (
    <Stack spacing={1.5} sx={{ border: 1, borderColor: "divider", p: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Button
          disabled={previous == null}
          onClick={() => {
            if (previous != null) onChange(previous);
          }}
        >
          {t("previousEvent")}
        </Button>
        <SafeDateTimePicker
          label={t("topologyViewAt")}
          timezone="UTC"
          ampm={false}
          value={dayjs.utc(at)}
          onInvalidDate={() => undefined}
          onChange={(time) => {
            if (time?.isValid()) onChange(time.toISOString());
          }}
        />
        <Button
          disabled={next == null}
          onClick={() => {
            if (next != null) onChange(next);
          }}
        >
          {t("nextEvent")}
        </Button>
        <Button
          onClick={() => {
            onChange(dayjs().toISOString());
          }}
        >
          {t("topologyNow")}
        </Button>
        <TextField
          select
          size="small"
          label={t("topologyWindow")}
          value={windowDays}
          onChange={(event) => {
            setWindowDays(Number(event.target.value));
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value={0}>{t("topologyAllHistory")}</MenuItem>
          {[1, 7, 30].map((days) => (
            <MenuItem key={days} value={days}>
              {days} {t(days === 1 ? "day" : "days")}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t("topologyCorrectedHistory")}
      </Typography>
      <Box sx={{ px: 1.5 }}>
        <Slider
          min={min}
          max={max}
          step={1000}
          value={cursor ?? selectedTime}
          aria-label={t("topologyTimeline")}
          valueLabelDisplay="auto"
          valueLabelFormat={(time) =>
            formatInstallationTimestamp(dayjs(time).toISOString())
          }
          onChange={(_, time) => {
            if (typeof time === "number") setCursor(time);
          }}
          onChangeCommitted={(_, time) => {
            setCursor(null);
            if (typeof time === "number") onChange(dayjs(time).toISOString());
          }}
        />
        <Box sx={{ height: 24, position: "relative" }}>
          {visibleTimes.map((time) => (
            <Button
              key={time}
              title={formatInstallationTimestamp(time)}
              aria-label={t("topologyEventAt", {
                time: formatInstallationTimestamp(time),
              })}
              onClick={() => {
                onChange(time);
              }}
              sx={{
                position: "absolute",
                left: `${percentage(installationInstant(time))}%`,
                transform: "translateX(-50%)",
                minWidth: 8,
                width: 8,
                height: 16,
                p: 0,
                borderRadius: 0,
                bgcolor: "primary.main",
                opacity: 0.65,
              }}
            />
          ))}
        </Box>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption">
            {formatInstallationTimestamp(dayjs(min).toISOString())}
          </Typography>
          <Typography variant="caption">
            {formatInstallationTimestamp(dayjs(max).toISOString())}
          </Typography>
        </Stack>
      </Box>
      {intervals.length > 0 && (
        <Box sx={{ maxHeight: 160, overflow: "auto" }}>
          {intervals.map((interval) => {
            const start = dayjs(interval.start).valueOf();
            const end =
              interval.end == null ? max : dayjs(interval.end).valueOf();
            if (end <= min || start > max) return null;
            return (
              <Stack
                key={interval.key}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ height: 28 }}
              >
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ width: 180, flexShrink: 0 }}
                >
                  {interval.label}
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    flex: 1,
                    height: 18,
                    bgcolor: "action.hover",
                  }}
                >
                  <Box
                    title={`${formatInstallationTimestamp(interval.start)} — ${interval.end == null ? t("topologyOpenEnded") : formatInstallationTimestamp(interval.end)}`}
                    sx={{
                      position: "absolute",
                      left: `${percentage(start)}%`,
                      width: `${percentage(end) - percentage(start)}%`,
                      height: "100%",
                      bgcolor: interval.colour,
                      opacity: 0.65,
                    }}
                  />
                </Box>
              </Stack>
            );
          })}
        </Box>
      )}
      {currentEvents.map((event) => (
        <Typography key={`${event.kind}-${event.version_id}`} variant="body2">
          {
            topology.sensors.find((sensor) => sensor.id === event.sensor_id)
              ?.name
          }
          : {t(EVENT_LABELS[event.kind])} — {event.label}
        </Typography>
      ))}
      {events.length === 0 && (
        <Typography color="text.secondary">{t("topologyNoEvents")}</Typography>
      )}
    </Stack>
  );
};
